import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Loader2, ShieldCheck, Zap, Download, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TIERS = [
  {
    name: "Free Assessment", price: "$0", period: "free forever", testid: "tier-explorer",
    description: "Discover where you'll thrive before making life-changing decisions.",
    features: [
      "Unlimited AI assessments", "Top 3 matching cities", "Compatibility score",
      "City highlights", "Basic city comparison", "Unlimited reassessments",
    ],
    cta: "Start Free Assessment", variant: "outline", action: "assessment",
  },
  {
    name: "Personal Relocation Report", price: "$17.99", period: "per report", testid: "tier-pro", highlight: true,
    description: "Everything you need to confidently choose your next city.",
    features: [
      "Everything in Free", "Complete AI relocation analysis", "Personalized recommendations",
      "Cost of living breakdown", "Climate & weather analysis", "Safety overview",
      "Healthcare comparison", "Career opportunities", "Tax insights", "Lifestyle compatibility",
      "Neighborhood recommendations", "Step-by-step relocation roadmap", "Premium downloadable PDF report",
    ],
    cta: "Unlock My Report", variant: "default", action: "reports",
  },
  {
    name: "Relocation Success Kit", price: "$49.99", period: "one-time", testid: "tier-concierge",
    description: "Everything you need to successfully plan and execute your relocation.",
    features: [
      "Everything in Personal Report", "AI Relocation Assistant", "30-day AI concierge access",
      "Personalized relocation checklist", "Visa & immigration guidance", "Required document checklist",
      "Priority support", "Follow-up relocation planning", "Future reassessments included",
    ],
    note: "Perfect for families, professionals and entrepreneurs planning an international move.",
    cta: "Start My Relocation Plan", variant: "outline", action: "kit",
  },
];

const TRUST = [
  { icon: ShieldCheck, label: "Secure Stripe payments" },
  { icon: Sparkles, label: "AI-powered personalized recommendations" },
  { icon: Zap, label: "Instant digital delivery" },
  { icon: BadgeCheck, label: "Money-back guarantee if report generation fails" },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingTier, setLoadingTier] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Verify payment when returning from a Success Kit checkout.
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    let active = true;
    let attempts = 0;
    setVerifying(true);
    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!active) return;
          setVerifying(false);
          await refreshUser();
          toast.success("Relocation Success Kit activated! Your AI concierge is ready.");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          navigate("/success-kit");
          return;
        }
        if (data.status === "expired") {
          if (!active) return;
          setVerifying(false);
          toast.error("Payment session expired. Please try again.");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        attempts += 1;
        if (attempts < 8) setTimeout(poll, 2000);
        else { setVerifying(false); toast.info("Still processing your payment. Refresh in a moment."); }
      } catch {
        if (active) { setVerifying(false); toast.error("Could not verify payment."); }
      }
    };
    poll();
    return () => { active = false; };
    // One-shot verification poll for the session_id present at mount; adding
    // searchParams/refreshUser deps would restart the poll and duplicate calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle = async (tier) => {
    if (tier.action === "assessment") return navigate("/assessment");
    if (tier.action === "reports") return navigate("/reports");
    if (tier.action === "kit") {
      setLoadingTier(tier.testid);
      try {
        const { data } = await api.post("/payments/checkout", {
          package_id: "success_kit",
          origin_url: window.location.origin,
        });
        window.location.href = data.url;
      } catch {
        toast.error("Could not start checkout");
        setLoadingTier(null);
      }
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-12 animate-fade-up">
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-500">
          <Sparkles className="h-3 w-3" /> Simple, transparent pricing
        </div>
        <h1 className="mt-4 font-heading text-4xl font-semibold tracking-tight sm:text-5xl">Find your place, your way</h1>
        <p className="mx-auto mt-3 max-w-lg text-slate-500">
          Start free. Upgrade only when you're ready for the full relocation playbook.
        </p>
      </div>

      {verifying && (
        <div className="mx-auto mt-6 flex max-w-md items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="payment-verifying">
          <Loader2 className="h-4 w-4 animate-spin" /> Verifying your payment…
        </div>
      )}

      <div className="mt-12 grid gap-6 lg:grid-cols-3">
        {TIERS.map((tier) => (
          <div
            key={tier.name}
            data-testid={tier.testid}
            className={`relative flex flex-col rounded-xl border bg-white p-7 ${tier.highlight ? "border-slate-900 shadow-sm" : "border-slate-200"}`}
          >
            {tier.highlight && (
              <div className="absolute -top-3 left-7 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white">Most popular</div>
            )}
            <h3 className="font-heading text-lg font-semibold tracking-tight">{tier.name}</h3>
            <p className="mt-1 text-sm text-slate-500">{tier.description}</p>
            <div className="mt-5 flex items-baseline gap-1">
              <span className="font-heading text-4xl font-semibold tracking-tight">{tier.price}</span>
              <span className="text-sm text-slate-500">/ {tier.period}</span>
            </div>
            <ul className="mt-6 space-y-3">
              {tier.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-slate-900" /> {f}
                </li>
              ))}
            </ul>
            {tier.note && (
              <p className="mt-5 rounded-md bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-500" data-testid={`${tier.testid}-note`}>
                {tier.note}
              </p>
            )}
            <Button
              data-testid={`${tier.testid}-cta`}
              variant={tier.variant}
              className="mt-7 h-11 w-full font-medium"
              disabled={loadingTier === tier.testid}
              onClick={() => handle(tier)}
            >
              {loadingTier === tier.testid ? <Loader2 className="h-4 w-4 animate-spin" /> : tier.cta}
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3" data-testid="trust-section">
        {TRUST.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-2 text-sm text-slate-600">
            <Icon className="h-4 w-4 text-slate-900" /> {label}
          </div>
        ))}
      </div>
    </div>
  );
}
